import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { MaturityBadge, ChevronSvg } from '../../Shared'
import { SmallSpinner } from '../styles'
import { getFrameworkRequirements, getPreviewFrameworkRequirements } from 'services/transparency-service'
import RequirementLabel from '../../../../Components/RequirementLabel'

const Wrapper = styled.div``

const EmptyText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-slate);
`

const FrameworkPaper = styled.div`
  margin-bottom: 12px;
  border-radius: 14px;
  overflow: hidden;
  background: white;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const FrameworkHeader = styled.div`
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  &:hover { background: var(--vo-capri-25); }
`

const HeaderLeft = styled.div`
  flex: 1;
`

const FrameworkTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
`

const FrameworkName = styled.h3`
  font-family: 'Maven Pro', sans-serif;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--vo-ink);
`

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ProgressTrack = styled.div`
  flex: 1;
  max-width: 240px;
  height: 8px;
  border-radius: 4px;
  background: var(--vo-cloud);
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${({ $rate }) =>
    $rate >= 50
      ? 'linear-gradient(90deg, var(--color-brand-strong), var(--vo-capri))'
      : 'linear-gradient(90deg, var(--vo-warning), var(--vo-warning))'};
  width: ${({ $rate }) => Math.min($rate, 100)}%;
  transition: width 0.3s ease;
`

const CompletionText = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-brand);
`

const ExpandBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-slate);
  border-radius: 50%;
  flex-shrink: 0;
  &:hover { background: var(--vo-mist); }
`

const RequirementsSection = styled.div`
  border-top: 1px solid var(--vo-cloud);
  padding: 16px 24px;
  background: var(--vo-capri-25);
`

const RequirementsTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 12px;
`

const RequirementsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`

const RequirementItem = styled.div`
  padding: 10px 14px;
  background: white;
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`

const EmptyRequirements = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-slate);
  padding: 16px 0;
  text-align: center;
`

const FrameworksTab = ({ frameworks = [], companyKey, accessToken, isPreview = false }) => {
  const { t } = useTranslation('transparency')
  const [expandedFramework, setExpandedFramework] = useState(null)

  const toggleFramework = (frameworkId) => {
    setExpandedFramework(expandedFramework === frameworkId ? null : frameworkId)
  }

  if (!frameworks || frameworks.length === 0) {
    return <EmptyText>{t('protected.emptyFrameworks')}</EmptyText>
  }

  return (
    <Wrapper>
      {frameworks.map((framework, index) => (
        <FrameworkCard
          key={framework.frameworkId || index}
          framework={framework}
          companyKey={companyKey}
          accessToken={accessToken}
          isPreview={isPreview}
          isExpanded={expandedFramework === framework.frameworkId}
          onToggle={() => toggleFramework(framework.frameworkId)}
        />
      ))}
    </Wrapper>
  )
}

const FrameworkCard = ({ framework, companyKey, accessToken, isPreview, isExpanded, onToggle }) => {
  const { t } = useTranslation('transparency')
  const { data: requirementsData, isLoading } = useQuery(
    ['frameworkRequirements', framework.frameworkId, companyKey, isPreview],
    () => isPreview
      ? getPreviewFrameworkRequirements(framework.frameworkId, companyKey)
      : getFrameworkRequirements(framework.frameworkId, companyKey, accessToken),
    { enabled: isExpanded && !!framework.frameworkId }
  )

  const requirements = requirementsData?.data?.requirements || []
  const completionRate = framework.completionRate || 0

  return (
    <FrameworkPaper>
      <FrameworkHeader onClick={onToggle}>
        <HeaderLeft>
          <FrameworkTop>
            <FrameworkName>{framework.name}</FrameworkName>
            <MaturityBadge label={framework.maturityLabel} />
          </FrameworkTop>
          <ProgressRow>
            <ProgressTrack>
              <ProgressFill $rate={completionRate} />
            </ProgressTrack>
            <CompletionText>
              {t('protected.frameworks.completion', { rate: completionRate })}
            </CompletionText>
          </ProgressRow>
        </HeaderLeft>
        <ExpandBtn>
          <ChevronSvg $expanded={isExpanded} />
        </ExpandBtn>
      </FrameworkHeader>

      {isExpanded && (
        <RequirementsSection>
          <RequirementsTitle>{t('protected.frameworks.requirements')}</RequirementsTitle>
          {isLoading ? (
            <LoadingRow><SmallSpinner /></LoadingRow>
          ) : requirements.length > 0 ? (
            <RequirementsGrid>
              {requirements.map((req, index) => (
                <RequirementItem key={req.id || index}>
                  <RequirementLabel code={req.sourceIdentifier ?? req.id} title={req.name} />
                  <MaturityBadge label={req.maturityLabel} />
                </RequirementItem>
              ))}
            </RequirementsGrid>
          ) : (
            <EmptyRequirements>{t('protected.emptyRequirements')}</EmptyRequirements>
          )}
        </RequirementsSection>
      )}
    </FrameworkPaper>
  )
}

export { FrameworksTab }
